import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Hammer, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { productionService } from '@services/productionService';
import {
  ProductionActivity,
  PRODUCTION_ACTIVITY_CATEGORIES,
  getActivityCategoryLabel,
} from '@/types/production';

// Cores dos badges por categoria
const CATEGORY_BADGE_CLASSES: Record<number, string> = {
  0: 'bg-blue-100 text-blue-800',
  1: 'bg-purple-100 text-purple-800',
  2: 'bg-amber-100 text-amber-800',
  3: 'bg-pink-100 text-pink-800',
  4: 'bg-cyan-100 text-cyan-800',
  5: 'bg-slate-100 text-slate-700',
};

/**
 * Listagem de atividades de produção (atelier de cabides)
 */
export const ActivityListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<ProductionActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Exclusão
  const [deleteTarget, setDeleteTarget] = useState<ProductionActivity | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await productionService.listActivities({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
        category: categoryFilter,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  };

  // Carrega quando página ou filtros mudam
  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, categoryFilter, statusFilter]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadItems();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await productionService.deleteActivity(deleteTarget.id);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir atividade');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Atividades de Produção</h1>
          <p className="text-slate-600">
            Gerencie as atividades do atelier (preparação, flocagem, corte, personalização...)
          </p>
        </div>
        <PermissionGuard permission="production.create">
          <Button onClick={() => navigate('/production/activities/new')} icon={<Plus size={20} />}>
            Nova Atividade
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por código ou nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={20} />}
              />
            </div>
            <div>
              <select
                className="input"
                value={categoryFilter === undefined ? '' : categoryFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setCategoryFilter(e.target.value === '' ? undefined : parseInt(e.target.value));
                }}
              >
                <option value="">Todas as categorias</option>
                {PRODUCTION_ACTIVITY_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className="input"
                value={statusFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                }}
              >
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
                <option value="all">Todas</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando atividades...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <Hammer className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhuma atividade encontrada' : 'Nenhuma atividade cadastrada'}
              </p>
              <PermissionGuard permission="production.create">
                <Button onClick={() => navigate('/production/activities/new')}>
                  Cadastrar primeira atividade
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Código</th>
                  <th className="text-left">Nome</th>
                  <th className="text-center">Categoria</th>
                  <th className="text-center">Unidade</th>
                  <th className="text-right">Tempo Estimado (min/un)</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">{item.code}</code>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{item.name}</p>
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          CATEGORY_BADGE_CLASSES[item.category] || 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {getActivityCategoryLabel(item.category)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-700">{item.unit || '-'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.estimated_minutes_per_unit !== undefined &&
                      item.estimated_minutes_per_unit !== null ? (
                        <span className="font-medium">
                          {item.estimated_minutes_per_unit.toLocaleString('pt-BR')}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={item.is_active ? 'success' : 'danger'}>
                        {item.is_active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="production.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/production/activities/${item.id}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="production.delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(item)}
                            icon={<Trash2 size={14} />}
                          >
                            Excluir
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Atividade"
        message={`Tem certeza que deseja excluir a atividade "${deleteTarget?.code} - ${deleteTarget?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleting}
      />
    </div>
  );
};
